import { useCallback, useEffect, useState } from 'react';
import { MessageSquareOff } from 'lucide-react';
import { toast } from 'sonner';
import { reviewsService } from '@/services/reviews.service';
import { ApiError } from '@/services/http';
import { useAccountStore } from '@/store/account.store';
import { useReadersStore } from '@/store/readers.store';
import { ReaderAvatar } from '@/components/ui/ReaderAvatar';
import { StarRating } from '@/components/ui/StarRating';
import { ReviewForm } from './ReviewForm';
import type { ReviewView } from '@/types/api';

/**
 * BookReviewsPanel — the reviews section embedded in the Details tab of
 * BookDetailModal. Lives below the "Similar books" carousel so the
 * existing hero / labels / description stay above the fold.
 *
 * Visibility rules (mirrors the parental-control toggle):
 *   - `account.commentsEnabled === false` hides the comments list for
 *     this family. The "your review" form still renders, but the
 *     comment textarea is replaced by a small note pointing at Account
 *     settings (the server independently rejects comment submissions
 *     for this family, so the UI gate is just for clarity).
 *
 * Ratings flow:
 *   - Submitting a star rating posts live and bumps the global average.
 *   - Submitting comment text holds it in 'pending'; the form shows an
 *     "awaiting approval" banner until an admin approves.
 *   - Editing an approved comment demotes it back to pending — the
 *     banner re-appears on next render.
 *
 * Hydration:
 *   - One round-trip for the public list, one for the caller's own
 *     review (when a reader is selected). Both fire in parallel.
 *   - `onAggregateChanged` lets the parent BookDetailModal refresh the
 *     hero badge when a new rating lands; we don't refetch the whole
 *     book just to update one number.
 */
export interface BookReviewsPanelProps {
  bookId: string;
  /**
   * Called whenever a write succeeds, with the freshly observed average
   * + count. The parent can repaint the AverageRatingBadge in the hero
   * without refetching the whole book.
   */
  onAggregateChanged?: (next: { averageRating: number | null; ratingCount: number }) => void;
}

export function BookReviewsPanel({ bookId, onAggregateChanged }: BookReviewsPanelProps) {
  const selectedReader = useReadersStore((s) => s.selectedReader());
  const account = useAccountStore((s) => s.account);
  const commentsVisible = account?.commentsEnabled !== false;

  const [reviews, setReviews] = useState<ReviewView[] | null>(null);
  const [ownReview, setOwnReview] = useState<ReviewView | null>(null);
  const [ownReviewLoaded, setOwnReviewLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  // After any successful write we recompute the aggregate from the
  // local list + the (possibly new) own review row. Cheaper than
  // round-tripping the books service.
  const recomputeAggregate = useCallback(
    (next: ReviewView[], own: ReviewView | null) => {
      // The public list excludes pending/rejected comments but always
      // includes the owner's own ratings-only or approved rows. The
      // owner's pending/rejected row, however, may not be in the
      // public list — we still want its rating included in the
      // average.
      const dedup = new Map<string, ReviewView>();
      for (const r of next) dedup.set(r.id, r);
      if (own) dedup.set(own.id, own);
      const ratings = Array.from(dedup.values()).map((r) => r.rating);
      const ratingCount = ratings.length;
      const averageRating =
        ratingCount === 0 ? null : ratings.reduce((sum, r) => sum + r, 0) / ratingCount;
      onAggregateChanged?.({ averageRating, ratingCount });
    },
    [onAggregateChanged],
  );

  useEffect(() => {
    let cancelled = false;
    setReviews(null);
    setOwnReview(null);
    setOwnReviewLoaded(false);

    (async () => {
      try {
        const [list, own] = await Promise.all([
          reviewsService.listForBook(bookId),
          selectedReader
            ? reviewsService.findOwn(selectedReader.id, bookId).then((r) => r.review)
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        setReviews(list);
        setOwnReview(own);
        setOwnReviewLoaded(true);
        // Notify the parent of the freshly-observed aggregate even on
        // the initial mount — otherwise the hero badge keeps showing
        // whatever stale value rode in on `book.averageRating` from the
        // Library cache. Without this, closing + reopening the modal
        // after a submit hides the badge until the next Library
        // refetch (e.g. a hard page reload).
        recomputeAggregate(list, own);
      } catch (err) {
        if (cancelled) return;
        // Don't toast — the section just renders empty. The book detail
        // works fine without reviews.
        console.warn('Reviews load failed:', err);
        setReviews([]);
        setOwnReview(null);
        setOwnReviewLoaded(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [bookId, selectedReader?.id]);

  const handleSubmit = async (params: { rating: number; commentText: string }) => {
    if (!selectedReader || busy) return;
    setBusy(true);
    try {
      const payload = {
        readerId: selectedReader.id,
        bookId,
        rating: params.rating,
        commentText: params.commentText.length > 0 ? params.commentText : undefined,
      };
      // POST upserts on (reader, book) so we don't branch on
      // create-vs-update — same call shape both ways.
      const updated = await reviewsService.create(payload);
      setOwnReview(updated);
      // Refetch the public list so newly-approved or just-promoted-to-
      // pending rows reflect immediately (status filtering is server-
      // side, so we trust the next list response).
      const next = await reviewsService.listForBook(bookId);
      setReviews(next);
      recomputeAggregate(next, updated);
      toast.success(
        params.commentText.length > 0
          ? 'Thanks! Your comment is awaiting approval.'
          : 'Thanks for rating!',
      );
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save your review.');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!ownReview || busy) return;
    setBusy(true);
    try {
      await reviewsService.remove(ownReview.id);
      setOwnReview(null);
      const next = await reviewsService.listForBook(bookId);
      setReviews(next);
      recomputeAggregate(next, null);
      toast.success('Review removed.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not remove review.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-10">
      <div className="or-kicker mb-1.5">Reader reviews</div>
      <h3 className="font-display text-[18px] font-bold text-or-slate-900">
        What readers are saying
      </h3>

      {/* --- Your review form --- */}
      <div className="mt-4 rounded-2xl border border-or-orange-100 bg-or-cream-50/60 p-5">
        {selectedReader ? (
          <ReviewForm
            existing={ownReviewLoaded ? ownReview : null}
            loading={!ownReviewLoaded}
            disabled={busy}
            commentsEnabled={commentsVisible}
            onSubmit={handleSubmit}
            onDelete={ownReview ? handleDelete : undefined}
          />
        ) : (
          <div className="text-[13px] text-or-slate-600">
            Pick a reader profile above to leave a rating or comment.
          </div>
        )}
      </div>

      {/* --- Comments list --- */}
      <div className="mt-6">
        {!commentsVisible ? (
          <div className="flex items-start gap-3 rounded-2xl border border-or-slate-100 bg-white p-4 text-[13px] text-or-slate-600">
            <MessageSquareOff className="mt-0.5 h-4 w-4 shrink-0 text-or-slate-400" aria-hidden />
            <div>
              Comments are turned off for this account. You can re-enable them anytime from
              <span className="font-semibold"> Account → Comments &amp; reviews</span>.
            </div>
          </div>
        ) : reviews === null ? (
          <ReviewsListSkeleton />
        ) : reviews.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-or-slate-200 bg-white p-5 text-center text-[13px] text-or-slate-500">
            No reviews yet — be the first to share what you think!
          </div>
        ) : (
          <ul className="flex flex-col gap-3">
            {reviews.map((review) => (
              <ReviewItem key={review.id} review={review} />
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function ReviewItem({ review }: { review: ReviewView }) {
  return (
    <li className="rounded-2xl border border-or-slate-100 bg-white p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <ReaderAvatar
          name={review.readerName}
          avatarSlug={review.readerAvatarSlug}
          size="sm"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-semibold text-or-slate-900">{review.readerName}</span>
            <StarRating value={review.rating} readOnly size="sm" />
            <span className="text-[12px] text-or-slate-500">
              {formatRelativeDate(review.createdAt)}
            </span>
          </div>
          {review.commentText ? (
            <p className="mt-1.5 whitespace-pre-wrap text-[14px] leading-relaxed text-or-slate-700">
              {review.commentText}
            </p>
          ) : (
            <p className="mt-1.5 text-[13px] italic text-or-slate-500">Star rating only.</p>
          )}
        </div>
      </div>
    </li>
  );
}

function ReviewsListSkeleton() {
  return (
    <ul className="flex flex-col gap-3">
      {[0, 1].map((i) => (
        <li
          key={i}
          className="animate-pulse rounded-2xl border border-or-slate-100 bg-white p-4"
        >
          <div className="flex gap-3">
            <div className="h-8 w-8 rounded-full bg-or-slate-100" />
            <div className="flex-1 space-y-2">
              <div className="h-3 w-1/3 rounded-full bg-or-slate-100" />
              <div className="h-3 w-2/3 rounded-full bg-or-slate-100" />
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

/**
 * Tiny relative-time formatter for review timestamps. We don't ship
 * date-fns on the client, and ICU's Intl.RelativeTimeFormat is
 * lightweight enough to use directly. Anything older than 30 days
 * falls back to the locale-formatted short date.
 */
function formatRelativeDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.round(diffMs / 60_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.round(diffH / 24);
  if (diffD < 30) return `${diffD}d ago`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

